/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * ⚠️ DEPRECATED: Legacy Interactive Story UI — DO NOT EDIT ⚠️
 * 
 * This screen is part of the deprecated interactive story journey.
 * It is NOT routed in production and kept only for backwards compatibility.
 * 
 * The LIVE production UI is the Hub System Assessment at /hub
 * 
 * DO NOT make changes to this file unless fixing security vulnerabilities.
 * All new features should be added to the Hub UI instead.
 */

/**
 * Screen 4: Your Two Sensible Options
 * Shows Option A (Best Overall Advice) and Option B (Strong Alternative)
 * With links to detailed system chapters (Layer 2)
 */

import DeepDive from '../../components/DeepDive';
import { deepDives } from '../../content/deepDiveContent';
import { getSystemChapterIdForOption, systemChapterContent } from '../../content/systemChapterContent';
import { useRecommendation } from '../../context/RecommendationContext';
import { useStoryContext } from '../../context/StoryContext';
import { getOptionDisplayTitle } from '../../utils/displayNames';
import { formatNumber, gbp, kg } from '../../utils/formatters';

function Screen4TwoOptions() {
  const { openSystemChapter } = useStoryContext();
  const { recommendationResult } = useRecommendation();
  const primaryOption = recommendationResult?.output?.primary;
  const alternativeOption = recommendationResult?.output?.alternatives?.[0];
  const year1Options = recommendationResult?.output?.year1?.options ?? [];

  const resolveChapterId = (option: typeof primaryOption, fallbackId: string) => {
    const mappedChapterId = getSystemChapterIdForOption(option?.boiler, option?.water, option?.systemType);
    const boilerLower = option?.boiler?.toLowerCase() || '';
    const systemTypeLower = option?.systemType?.toLowerCase() || '';
    const isHeatPump = systemTypeLower.includes('heat_pump')
      || boilerLower.includes('heat_pump')
      || boilerLower.includes('ashp')
      || boilerLower.includes('gshp');

    if (mappedChapterId && systemChapterContent[mappedChapterId]) {
      return { chapterId: mappedChapterId, disableExplore: false };
    }

    if (isHeatPump) {
      console.warn('Heat pump option missing source for system chapter mapping.', option);
      return { chapterId: null, disableExplore: true };
    }

    if (option?.id && systemChapterContent[option.id]) {
      return { chapterId: option.id, disableExplore: false };
    }

    return { chapterId: fallbackId, disableExplore: false };
  };

  const primaryChapter = resolveChapterId(primaryOption, 'system_cylinder');
  const alternativeChapter = resolveChapterId(alternativeOption, 'combi');

  const primaryTitle = primaryOption ? getOptionDisplayTitle(primaryOption) : 'System Boiler with Cylinder';
  const alternativeTitle = alternativeOption ? getOptionDisplayTitle(alternativeOption) : 'Combi Boiler';

  const primaryRationale = primaryOption?.why || 'Best balance of efficiency, hot water performance, and long-term value for your home';
  const alternativeRationale = alternativeOption?.why || 'More compact installation with lower upfront cost and simpler maintenance';

  const primaryImpact = year1Options.find((option) => option.optionId === primaryOption?.id)?.impact;
  const alternativeImpact = year1Options.find((option) => option.optionId === alternativeOption?.id)?.impact;

  return (
    <div className="interactive-story-screen">
      <h1 className="screen-title">Your Two Options</h1>

      <div className="screen-body">
        <p className="screen-text">
          Based on your situation, we&apos;ve identified two systems that make sense for you.
        </p>

        <div className="options-container">
          <div className="option-card primary">
            <div className="option-badge star">⭐ Best Overall</div>
            <h2 className="option-title">Option A</h2>
            <div className="option-system-name">{primaryTitle}</div>
            <p className="option-rationale">
              {primaryRationale}
            </p>
            <ul className="option-metrics">
              <li>Running cost: {primaryImpact ? gbp(primaryImpact.costGbp) : 'Not enough info yet'}</li>
              <li>Energy use: {primaryImpact ? `${formatNumber(primaryImpact.totalHeatKwh)} kWh` : 'Not enough info yet'}</li>
              <li>Carbon impact: {primaryImpact ? kg(primaryImpact.carbonKg) : 'Not enough info yet'}</li>
            </ul>
            <button
              className="option-explore-button"
              onClick={() => primaryChapter.chapterId && openSystemChapter(primaryChapter.chapterId)}
              disabled={primaryChapter.disableExplore}
            >
              Explore this system →
            </button>
          </div>

          <div className="option-card secondary">
            <div className="option-badge balance">⚖️ Alternative</div>
            <h2 className="option-title">Option B</h2>
            <div className="option-system-name">{alternativeTitle}</div>
            <p className="option-rationale">
              {alternativeRationale}
            </p>
            <ul className="option-metrics">
              <li>Running cost: {alternativeImpact ? gbp(alternativeImpact.costGbp) : 'Not enough info yet'}</li>
              <li>Energy use: {alternativeImpact ? `${formatNumber(alternativeImpact.totalHeatKwh)} kWh` : 'Not enough info yet'}</li>
              <li>Carbon impact: {alternativeImpact ? kg(alternativeImpact.carbonKg) : 'Not enough info yet'}</li>
            </ul>
            <button
              className="option-explore-button"
              onClick={() => alternativeChapter.chapterId && openSystemChapter(alternativeChapter.chapterId)}
              disabled={alternativeChapter.disableExplore}
            >
              Explore this system →
            </button>
          </div>
        </div>

        <div className="screen-text secondary">
          Both options will work well. Your choice depends on whether you prioritize upfront simplicity or long-term performance.
          <DeepDive
            trigger="When might the other suit better?"
            content={deepDives.whenAlternativeSuits}
          />
        </div>
      </div>
    </div>
  );
}

export default Screen4TwoOptions;
